import { Injectable } from '@angular/core';
import { StorageService, StorageServiceKey } from './storage.service';
import jwt_decode from 'jwt-decode';
import { IJWTPayload, IJWTPayloadDecoded } from '../model/auth.model';
import { logger } from '../util/Logger';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

const className = "JwtService";

@Injectable()
export class JwtService {

  private currentJwtString = '';
  private currentJwtRefreshString = '';
  private currentJwtTypeString = '';

  // currentJwtPayload should reflect the value currently stored in cookies and be used as a proxy for the current known JWT payload
  private currentJwtPayloadSubject = new BehaviorSubject<IJWTPayloadDecoded | null | undefined>(undefined);

  public currentJwtPayload$ = this.currentJwtPayloadSubject.asObservable().pipe(
    filter(init => init !== undefined),
    distinctUntilChanged((oldVal, newVal) => JSON.stringify(oldVal) === JSON.stringify(newVal)),
    map(payload => payload as IJWTPayloadDecoded | null)
  );

  constructor(
    public storageService: CookieService,
    private localstorageSevice: StorageService
  ) {
    const signature = className + '.constructor: ';

    this.currentJwtString = this.readPersistedValue(StorageServiceKey.jwt);
    this.currentJwtRefreshString = this.readPersistedValue(StorageServiceKey.jwt_refresh);
    this.currentJwtTypeString = this.readPersistedValue(StorageServiceKey.jwt_token_type);

    const jwt = this.decodeJWT();
    // Loads the last known JWT into the service on init, even if its null

    logger.silly(signature + 'Updating Payload');
    this.currentJwtPayloadSubject.next(jwt);
  }

  public saveJWTData(jwtPayload: IJWTPayload): boolean {
    const signature = className + ".saveJWTData: ";
    /**
     * Verify jwt before saving
     */
    const payload = this.decodeJWT(jwtPayload.accessToken);

    if (!payload) {
      logger.silly(signature + "Jwt was not valid and was rejected");
      return false;
    }

    const payloadVerified = this.verifyJWT(payload);

    if (!payloadVerified) {
      logger.silly(signature + "Jwt did not pass verification and was rejected");
      return false;
    }

    const shouldUseSecureCookies = environment.production || window.location.protocol === 'https:';

    this.storageService.set(StorageServiceKey.jwt, jwtPayload.accessToken, 30, undefined, undefined, shouldUseSecureCookies, 'Strict');
    this.storageService.set(StorageServiceKey.jwt_refresh, jwtPayload.refreshToken, 30, undefined, undefined, shouldUseSecureCookies, 'Strict');
    this.storageService.set(StorageServiceKey.jwt_token_type, jwtPayload.tokenType, 30, undefined, undefined, shouldUseSecureCookies, 'Strict');

    this.currentJwtString = jwtPayload.accessToken;
    this.currentJwtRefreshString = jwtPayload.refreshToken;
    this.currentJwtTypeString = jwtPayload.tokenType;

    if (payload.punchoutToken) {
      this.localstorageSevice.setItem(StorageServiceKey.punchout_token, payload.punchoutToken);
    }

    logger.silly(signature + 'Setting Payload');
    this.currentJwtPayloadSubject.next(payload);

    return true;
  };

  public getJWTString(): string {
    if (this.currentJwtString) {
      return this.currentJwtString;
    }

    this.currentJwtString = this.readPersistedValue(StorageServiceKey.jwt);
    return this.currentJwtString;
  };

  public getJWTRefreshString(): string {
    if (this.currentJwtRefreshString) {
      return this.currentJwtRefreshString;
    }

    this.currentJwtRefreshString = this.readPersistedValue(StorageServiceKey.jwt_refresh);
    return this.currentJwtRefreshString;
  };

  public getJWTTypeString(): string {
    if (this.currentJwtTypeString) {
      return this.currentJwtTypeString;
    }

    this.currentJwtTypeString = this.readPersistedValue(StorageServiceKey.jwt_token_type);
    return this.currentJwtTypeString;
  };

  public decodeJWT(token?: string): IJWTPayloadDecoded | null {
    const signature = className + '.decodeJWT: ';
    const jwt = token || this.getJWTString();
    let payload: IJWTPayloadDecoded;

    if (!jwt) {
      logger.silly(signature + 'JWT is null');
      return null;
    }

    try {
      logger.silly(signature + `Decoding JWT[${jwt}]`);
      payload = jwt_decode(jwt);
    } catch (e) {
      logger.silly(signature + 'Decoding Error');
      console.log(e);
      return null;
    }

    if (!payload) {
      logger.silly(signature + 'Payload is null');
      return null;
    }

    return payload;
  };

  public verifyJWT(token?: string | IJWTPayloadDecoded | null): boolean {
    const signature = className + '.verifyJWT: ';
    // logger.silly(signature + `Verifying using token From[${(token ? 'args' : 'decode')}]`);
    const payload = token ? typeof token === 'string' && this.decodeJWT(token) || (token as IJWTPayloadDecoded) : this.decodeJWT();

    if (!payload) {
      logger.silly(signature + 'Payload is null');
      return false;
    }

    if (payload.exp < Math.round(new Date().getTime() / 1000)) {
      logger.silly(signature + 'Payload is expired');
      return false;
    }

    return true;
  };

  public removeJWTData() {
    const signature = className + '.removeJWTData: ';
    this.storageService.delete(StorageServiceKey.jwt);
    this.storageService.delete(StorageServiceKey.jwt_refresh);
    this.storageService.delete(StorageServiceKey.jwt_token_type);
    this.localstorageSevice.removeItem(StorageServiceKey.punchout_token);
    this.currentJwtString = '';
    this.currentJwtRefreshString = '';
    this.currentJwtTypeString = '';
    logger.silly(signature + 'Cleared');
    this.currentJwtPayloadSubject.next(null);
  };

  private readPersistedValue(key: StorageServiceKey): string {
    const cookieValue = this.storageService.get(key);

    if (cookieValue) {
      return cookieValue;
    }

    return '';
  }

  public isExpired(token?: string | IJWTPayloadDecoded): boolean {
    const payload = token ? typeof token === 'string' && this.decodeJWT(token) || (token as IJWTPayloadDecoded) : this.decodeJWT();

    if (!payload) {
      return false;
    }

    const now = new Date();
    const nowSeconds = Math.round(now.getTime() / 1000);

    if (payload.exp < nowSeconds) {
      return true;
    }

    return false;
  };
}
